import type { ColumnDef } from '@tanstack/react-table';
import {
  createSerialColumn,
  DrillDownCountButton,
  getAreaColumn,
  MemberNameCell,
  StackedCellText,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatCount, formatNumber } from '@/utils/number';
import { LEAVE_REPORT_DATE_COUNT_KEY } from '../constants/leaveReport.constants';
import type { LeaveReportDateCategory, LeaveReportItem } from '../types/leaveReport.types';

export const EMPLOYEE_COLUMN_ID = 'employee';

const DATE_UNIT = 'date';

interface DateCountColumn {
  category: LeaveReportDateCategory;
  header: string;
}

const DATE_COUNT_COLUMNS: DateCountColumn[] = [
  { category: 'absent', header: 'Absent (Days)' },
  { category: 'weekend', header: 'Weekends & Govt Holiday' },
  { category: 'total', header: 'Total Days' },
  { category: 'applied', header: 'Applied Days' },
  { category: 'unapplied', header: 'Unapplied Days' },
];

export function getEmployeeName(row: LeaveReportItem): string {
  return row.employeeName ?? EMPTY_VALUE;
}

export function getDateCategoryCount(
  row: LeaveReportItem,
  category: LeaveReportDateCategory,
): number {
  return row[LEAVE_REPORT_DATE_COUNT_KEY[category]];
}

function toDateCountColumn(
  { category, header }: DateCountColumn,
  onOpenDates: (row: LeaveReportItem, category: LeaveReportDateCategory) => void,
): ColumnDef<LeaveReportItem, unknown> {
  return {
    id: category,
    header,
    cell: ({ row }) => {
      const count = getDateCategoryCount(row.original, category);
      return (
        <DrillDownCountButton
          count={count}
          hint={`View ${formatCount(count, DATE_UNIT)}`}
          ariaLabel={`${header} of ${getEmployeeName(row.original)}: view ${formatCount(count, DATE_UNIT)}`}
          onOpen={() => onOpenDates(row.original, category)}
        />
      );
    },
  };
}

export function getLeaveReportColumns(
  onOpenDates: (row: LeaveReportItem, category: LeaveReportDateCategory) => void,
  searchText: string | undefined,
): ColumnDef<LeaveReportItem, unknown>[] {
  return [
    createSerialColumn<LeaveReportItem>(),
    {
      id: EMPLOYEE_COLUMN_ID,
      header: 'Employee Name',
      cell: ({ row }) => (
        <MemberNameCell
          member={{
            name: getEmployeeName(row.original),
            hrEmpId: row.original.hrEmpId,
            mobile: null,
          }}
          searchText={searchText}
        />
      ),
    },
    {
      id: 'organization',
      header: 'Company / Department',
      cell: ({ row }) => (
        <StackedCellText
          primary={row.original.company ?? <EmptyValue />}
          secondary={row.original.department}
        />
      ),
    },
    getAreaColumn<LeaveReportItem>(
      (row) => ({
        divisionName: row.division,
        districtName: row.district,
        thanaName: row.thana,
      }),
      { isCommaSeparated: true },
    ),
    {
      id: 'present',
      header: 'Present (Days)',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatNumber(row.original.presentDays)}</span>
      ),
    },
    ...DATE_COUNT_COLUMNS.map((column) => toDateCountColumn(column, onOpenDates)),
  ];
}
